import styled from '@emotion/styled'

import { metrics } from 'component-library/__styling/settings'
import { breakpoint, calcRem } from 'component-library/__styling/style-functions'
import { supports } from 'component-library/__helpers/at-supports-helper'

import { styledListClassName } from 'component-library/typography/List/List.styled'

export interface Props {
    isHeroImage?: boolean
}

const gridGapSize = calcRem(metrics.thewest.margins.md)
const insetMargin = calcRem(metrics.thewest.margins.xl)
const insetMarginMob = 0

export const StyledGrid = styled('article')(
    {
        margin: '0 auto',
        width: '100%',
        display: 'table', // Grid FallBack
        tableLayout: 'fixed', // Required for IE so content does not overflow
        marginBottom: metrics.thewest.margins.md,
    },
    // If grid is supported use css grid
    supports('display: grid', {
        display: 'grid', // Override display: table
        gridTemplateColumns: '100%',

        '> .row': {
            display: 'table-cell',
        },
    })
)

export const StyledCell = styled('div')(
    {
        display: 'block',
        padding: 0,
        paddingBottom: gridGapSize,
    },
    supports('display: grid', {
        padding: 0,
    })
)

export const StyledInsetCell = styled('div')(
    {
        display: 'block',
        padding: 0,
        paddingBottom: gridGapSize,

        [breakpoint('md')]: {
            padding: `0 ${insetMargin}`,
            marginBottom: gridGapSize,
        },

        // If grid supported remove bottom padding - this comes from gridGap
    },
    supports('display: grid', {
        padding: '0',
        paddingBottom: gridGapSize,

        [breakpoint('md')]: {
            padding: `0 ${insetMargin}`,
        },
    })
)

export const StyledFlexCell = styled(StyledCell)({
    display: 'flex',

    '& > div': {
        flexBasis: '50%',
        flexGrow: 1,
    },
})

export const StyledArticleContentFooter = styled('div')({
    marginLeft: insetMarginMob,
    marginRight: insetMarginMob,

    [breakpoint('md')]: {
        marginLeft: insetMargin,
        marginRight: insetMargin,
    },
})
StyledArticleContentFooter.displayName = 'ArticleContentFooter'

export const StyledArticleContent = styled('div')({
    /*
            - Adds the horizontal padding to the article content
            - Excludes the figure component so we can center images & video of unknown width
              but also have the image asset full bleed to the edge on desktop.
        */
    width: '100%',

    '&>div > *:not(figure)': {
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingLeft: insetMarginMob,
        paddingRight: insetMarginMob,

        [breakpoint('md')]: {
            paddingLeft: insetMargin,
            paddingRight: insetMargin,
        },
    },

    [`& .${styledListClassName}`]: {
        paddingLeft: insetMargin,

        [breakpoint('md')]: {
            paddingLeft: calcRem(metrics.thewest.margins.xl + metrics.thewest.margins.lg),
        },
    },

    '& > *:last-child': {
        marginBottom: 0,
    },

    '& figure, & blockquote': {
        marginBottom: calcRem(metrics.thewest.margins.xxl),
    },

    '& h2': {
        fontSize: calcRem(30),
        lineHeight: 1.167,
    },

    // If the last item in an article is an image then remove the border on the figcaption so it doesn't double up with topics
    '& > figure:last-child figcaption': {
        borderBottom: 'none',
    },

    '& .twin-image-wrapper': {
        marginBottom: calcRem(metrics.thewest.margins.md),

        [breakpoint('sm')]: {
            marginBottom: 0,
        },
    },
})
